import type { Settings } from '@ais-ui/websockets';
import type {
  CollectionTranslator,
  DomainTranslator,
  SettingsDomain,
} from './types';
import {
  useCollectionDomainSettings,
  type CollectionDomainOptions,
  type CollectionSettingsResult,
} from './useCollectionDomainSettings';
import {
  useDomainSettings,
  type DomainSettingsResult,
  type ExtraBlobPatch,
} from './useDomainSettings';

/**
 * DRY factory for per-domain "document" settings hooks. Every document domain
 * (core, map, vessels, …) reduces to the same thing: bind the generic resilient
 * engine to a domain name + its translator and expose a typed hook. Keeping this
 * in one place means the resilience/dual-write behaviour is defined exactly once.
 */
export const createDocumentDomainHook =
  <T>(domain: SettingsDomain, translator: DomainTranslator<T>) =>
  (): DomainSettingsResult<T> =>
    useDomainSettings<T>(domain, translator);

/**
 * Convenience factory for an "update only" hook derived from a document hook —
 * for callers (e.g. forms) that only write and never read.
 */
export const createDomainUpdateHook =
  <T>(useDomain: () => DomainSettingsResult<T>) =>
  (): ((
    patch: Partial<T>,
    extraBlobPatch?: ExtraBlobPatch,
  ) => Promise<Settings | undefined>) =>
    useDomain().update;

/**
 * DRY factory for per-domain "collection" settings hooks. The collection analog
 * of {@link createDocumentDomainHook}: bind the generic resilient collection
 * engine to a domain name, its `collection_path`, and its translator.
 */
export const createCollectionDomainHook =
  <TItem extends { id: string }>(
    domain: SettingsDomain,
    collectionPath: string,
    translator: CollectionTranslator<TItem>,
    options?: CollectionDomainOptions,
  ) =>
  (): CollectionSettingsResult<TItem> =>
    useCollectionDomainSettings<TItem>(
      domain,
      collectionPath,
      translator,
      options,
    );
